<template>
  <!-- Nothing renders until the stored token has been confirmed (or found
       absent) against the backend -- otherwise a signed-in picker would see
       the sign-in screen flash before their session comes back. -->
  <div v-if="!authState.ready" class="flex min-h-screen items-center justify-center">
    <div class="h-8 w-8 animate-spin rounded-full border-2 border-slate-700 border-t-emerald-400"></div>
  </div>

  <!-- A switched-off account (users.active, DEV-212). The whole shell goes --
       no header, no tabs -- so every route, reload or typed URL reads as an
       outage rather than a ban. Checked before sign-in: the token is kept. -->
  <div
    v-else-if="authState.unavailable"
    class="flex min-h-screen items-center justify-center bg-brand-bg px-4 text-center"
    role="alert"
  >
    <p class="max-w-xs text-base text-slate-300">Something went wrong. Please try again later.</p>
  </div>

  <SignIn v-else-if="!authState.user" />

  <div v-else id="app" class="min-h-screen bg-brand-bg text-white">
    <!-- Top bar: the wordmark always; full nav only where there is room. -->
    <header
      class="app-header fixed inset-x-0 top-0 z-50 border-b border-white/[0.08] bg-brand-navy"
    >
      <div
        class="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
      >
        <router-link to="/" class="brand" aria-label="Five Picks — home">
          <img src="/wordmark.png" alt="Five Picks" class="h-12 w-auto md:h-14" />
        </router-link>

        <div class="flex items-center gap-4">
          <nav class="hidden items-center gap-1 md:flex" aria-label="Primary">
            <router-link
              v-for="tab in tabs"
              :key="tab.to"
              :to="tab.to"
              class="nav-link"
              :class="{ 'nav-link--active': isActive(tab.to) }"
            >
              {{ tab.label }}
            </router-link>
          </nav>

          <!-- A single click here used to sign out immediately -- picked
               that name to check whose account it is, not to leave. Opening
               a menu instead makes sign-out its own deliberate second tap. -->
          <div ref="userMenu" class="relative">
            <button
              type="button"
              class="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 text-xs font-medium text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-white"
              :title="authState.user.email"
              :aria-expanded="userMenuOpen"
              aria-haspopup="menu"
              @click="userMenuOpen = !userMenuOpen"
            >
              <img
                v-if="authState.user.avatar_url"
                :src="authState.user.avatar_url"
                alt=""
                class="h-6 w-6 rounded-full"
              />
              <span class="max-w-[6rem] truncate">{{ authState.user.name || authState.user.email }}</span>
            </button>

            <div
              v-if="userMenuOpen"
              role="menu"
              class="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-white/10 bg-brand-navy py-1.5 shadow-xl"
            >
              <p class="truncate px-3 pb-1.5 pt-1 text-xs text-slate-500">{{ authState.user.email }}</p>

              <!-- Admin-only. Freezing the week's lines is a single-picker
                   action, so it lives here rather than in the primary nav. -->
              <router-link
                v-if="authState.user.admin"
                to="/confirm"
                role="menuitem"
                class="block px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-white/[0.06]"
                @click="userMenuOpen = false"
              >
                Confirm Lines
              </router-link>
              <router-link
                v-if="authState.user.admin"
                to="/admin/health"
                role="menuitem"
                class="block px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-white/[0.06]"
                @click="userMenuOpen = false"
              >
                Data Health
              </router-link>
              <router-link
                v-if="authState.user.admin"
                to="/admin/users"
                role="menuitem"
                class="block px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-white/[0.06]"
                @click="userMenuOpen = false"
              >
                Users
              </router-link>

              <button
                type="button"
                role="menuitem"
                class="w-full px-3 py-2 text-left text-sm text-slate-200 transition-colors hover:bg-white/[0.06]"
                @click="signOut"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="app-main mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <router-view />
    </main>

    <!-- Bottom tab bar: phone only. Nav lives in the thumb zone, not behind a
         hamburger, and clears the home indicator via safe-area padding. -->
    <nav
      class="tabbar fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-brand-navy/90 backdrop-blur-xl md:hidden"
      aria-label="Primary"
    >
      <div class="grid grid-cols-4">
        <router-link
          v-for="tab in tabs"
          :key="tab.to"
          :to="tab.to"
          class="tab"
          :class="{ 'tab--active': isActive(tab.to) }"
          :aria-current="isActive(tab.to) ? 'page' : undefined"
        >
          <span class="tab__pip" aria-hidden="true"></span>
          <svg
            class="tab__icon"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.7"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" :d="tab.icon" />
          </svg>
          <span class="tab__label">{{ tab.short }}</span>
        </router-link>
      </div>
    </nav>
  </div>
</template>

<script>
import SignIn from "@/components/SignIn.vue";
import auth from "@/utils/auth";

export default {
  name: "App",
  components: { SignIn },
  data() {
    return {
      authState: auth.state,
      userMenuOpen: false,
      tabs: [
        {
          to: "/",
          label: "Home",
          short: "Home",
          icon: "m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25",
        },
        {
          to: "/picks",
          label: "Picks",
          short: "Picks",
          icon: "M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
        },
        {
          to: "/history",
          label: "Results",
          short: "Results",
          icon: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5",
        },
        {
          to: "/leaderboard",
          label: "Pool",
          short: "Pool",
          icon: "M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0",
        },
      ],
    };
  },
  mounted() {
    auth.restoreSession();
    document.addEventListener("pointerdown", this.closeUserMenuOnOutside);
  },
  beforeUnmount() {
    document.removeEventListener("pointerdown", this.closeUserMenuOnOutside);
  },
  methods: {
    // Vue Router's own `router-link-active` matches by prefix, so "/" would
    // light up on every route. Match exactly at the root, by prefix elsewhere.
    isActive(path) {
      // Exact match at the root so "/" does not light up on every route;
      // prefix match elsewhere. /games/:id is a leaf reached from Home and
      // sits under no tab.
      if (path === "/") return this.$route.path === "/";
      return this.$route.path.startsWith(path);
    },
    signOut() {
      this.userMenuOpen = false;
      auth.signOut();
    },
    closeUserMenuOnOutside(event) {
      if (this.userMenuOpen && !this.$refs.userMenu?.contains(event.target)) {
        this.userMenuOpen = false;
      }
    },
  },
};
</script>

<style>
/* Global styles (unscoped) */
html,
body {
  margin: 0;
  padding: 0;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  background-color: #071425;
  color: #f9fafb;
  min-height: 100vh;
  /* `clip`, not `hidden`: `overflow-x: hidden` computes `overflow-y` to
     `auto`, which makes this a scroll container and silently breaks every
     `position: sticky` descendant. `clip` still clips sideways without
     creating one. */
  overflow-x: clip;
  /* iOS: stop Safari inflating type in landscape, and kill the grey flash on
     every tap so the app does not feel like a web page. */
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: transparent;
}

* {
  box-sizing: border-box;
}

/* No long-press link preview. iOS renders the previewed route in a
   preview-sized web view, and tapping the preview promotes that same view to
   full screen with its layout still sized to the preview: the fixed tab bar
   sat off screen and the games list could not scroll to its end, on the game
   page and every page reached from it. A tap still opens the link. */
a {
  -webkit-touch-callout: none;
}

/* The one source of truth for how tall the fixed header is. Anything that
   pins itself below the header -- the filter bar on Home, `.app-main`'s top
   padding -- reads this instead of hard-coding a number, because the notch
   inset makes the real height device-dependent. */
:root {
  --app-header-h: calc(3.5rem + env(safe-area-inset-top));
}
</style>

<style scoped>
#app {
  min-height: 100vh;
  background-color: #071425;
  position: relative;
  /* See the note on html/body above -- `hidden` here would break sticky too. */
  overflow-x: clip;
}

/* ---- Safe areas -------------------------------------------------------
   These resolve to 0 everywhere except a notched iPhone, where they keep the
   header clear of the Dynamic Island and the tab bar clear of the home
   indicator. They need `viewport-fit=cover` in public/index.html to do
   anything at all. */

.app-header {
  padding-top: env(safe-area-inset-top);
}

.tabbar {
  padding-bottom: env(safe-area-inset-bottom);
}

.app-main {
  /* header + breathing room */
  padding-top: calc(var(--app-header-h) + 1.25rem);
  /* tab bar (3.5rem) + home indicator, so nothing hides behind it */
  padding-bottom: calc(4.5rem + env(safe-area-inset-bottom));
}

@media (min-width: 768px) {
  .app-main {
    padding-bottom: 3rem;
  }
}

/* ---- Brand mark ------------------------------------------------------- */

.brand {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
}

.brand:active {
  opacity: 0.7;
}

/* ---- Desktop nav ------------------------------------------------------ */

.nav-link {
  position: relative;
  border-radius: 9999px;
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.nav-link:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.06);
}

.nav-link--active {
  color: #34ef93;
  background-color: rgba(52, 239, 147, 0.1);
  box-shadow: inset 0 0 0 1px rgba(52, 239, 147, 0.25);
}

/* ---- Bottom tab bar --------------------------------------------------- */

.tab {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  /* Apple HIG puts the minimum tap target at 44px; this clears it. */
  min-height: 3.5rem;
  padding: 0.5rem 0.25rem;
  color: #64748b;
  text-decoration: none;
  transition: color 0.2s ease;
}

.tab__icon {
  height: 1.5rem;
  width: 1.5rem;
  transition: transform 0.15s ease;
}

.tab__label {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
}

.tab--active {
  color: #34ef93;
}

.tab:active .tab__icon {
  transform: scale(0.88);
}

/* Little glowing bar over the active tab -- reads as "you are here" without
   needing a filled-in background. */
.tab__pip {
  position: absolute;
  top: 0;
  left: 50%;
  height: 2px;
  width: 0;
  transform: translateX(-50%);
  border-radius: 9999px;
  background-color: #34ef93;
  transition: width 0.25s ease;
}

.tab--active .tab__pip {
  width: 2rem;
  box-shadow: 0 0 12px rgba(52, 239, 147, 0.7);
}

@media (prefers-reduced-motion: reduce) {
  .tab__icon,
  .tab__pip,
  .nav-link,
  .brand {
    transition: none;
  }
}
</style>
